import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getProviderProfiles } from "../../../../../api/admin/provider-management";
import { routes } from "../../../../../constants/routes";
import { PROVIDER_BUSINESS_TYPES } from "../../../../../utils/constants";
import { useSetLoading } from "../../../../../common/hooks/appearance";
import ProfileHeader from "../../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import ListingTable from "../../../../../common/components/listing-table/list-table";
import AdminPaginationComponent from "../../../../../common/components/admin-pagination-component/admin-pagination-component";
import { AlertComponent } from "../../../../../common/components";
import ProviderRequestSearchAndFilters from "../provider-requests/search-and-filters";
import ProviderProfilesFilterOptions from "./provider-profiles-filters-options/provider-profiles-filter-options";
import {
  profileHeaders,
  providerProfileFilterOptions,
} from "./provider-profiles-helper";
import "./provider-profiles-list.scss";

const PAGE_SIZE = 10;

const ProviderProfilesList: React.FC = () => {
  /* -------------------- ROUTING -------------------- */
  const navigate = useNavigate();
  const location = useLocation();

  /* -------------------- STATES -------------------- */
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [searchText, setSearchText] = useState<string>(
    location?.state?.searchText || ""
  );
  const [isResetSearch, setIsResetSearch] = useState<boolean>(false);

  const [activeFilters, setActiveFilters] = useState<any>(
    location?.state?.activeFilters || {}
  );
  const [providerProfilesFilterOptions, setProviderProfilesFilterOptions] =
    useState<any[]>(providerProfileFilterOptions);

  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);

  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);

  /* -------------------- BREADCRUMB -------------------- */
  const breadcrumbLinkForList = [
    {
      label: "Provider Management",
      link: routes.PROVIDER_MANAGEMENT,
      handleClick: () => { },
    },
    {
      label: "Provider Profile",
      link: "#",
      handleClick: () => { },
    },
  ];

  /* -------------------- REFS -------------------- */
  const searchRef = useRef<{ clear: () => void }>(null);
  const isSearchTriggered = useRef<boolean>(false);

  /* -------------------- HANDLERS -------------------- */

  const fetchProviderProfiles = async (
    page: number,
    search: string,
    filters: any
  ) => {
    setLoading(true);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const params: any = {
        page,
        limit: PAGE_SIZE,
        offset,
        search: search,
      };

      if (filters?.businessName) {
        params.businessName = filters.businessName;
      }


      params.businessTypes =
        filters?.businessType && filters.businessType.length
          ? filters.businessType
          : PROVIDER_BUSINESS_TYPES;
      const response = await getProviderProfiles(params);
      if (response?.status === 200 || response?.data?.success) {
        setData(response?.data?.data?.data || []);
        setTotalRecords(response?.data?.data?.totalCount || 0);
      } else {
        setAlertType("error");
        setAlertMessage("Failed to fetch provider requests.");
        setAlertOpen(true);
      }
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch provider requests."
      );
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search handler
  const handleSearch = useCallback(
    _.debounce((text: string) => {
      isSearchTriggered.current = true;
      setCurrentPage(1); // Reset to page 1 on new search
      setSearchText(text);

      if (text === "") {
        setIsResetSearch(true);
        setTimeout(() => setIsResetSearch(false), 100);
      }
    }, 500),
    []
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  /* -------------------- MEMOIZED DATA -------------------- */

  // Format API data for table rendering
  const formattedData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.map((item: any) => ({
      id: item.id,
      businessName: item.businessName,
      primaryContactName: item.primaryContactName,
      businessType: item.businessType,
      businessPhone: item.businessPhone,
      businessEmail: item.businessEmail,
    }));
  }, [data]);

  /* -------------------- APPLIED FILTER COUNT -------------------- */
  const appliedFilterCount = useMemo(() => {
    return Object.values(activeFilters).filter(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    ).length;
  }, [activeFilters]);

  // No-results message based on search/filter state
  const noResultsTxt = useMemo(() => {
    if (loading) return "Loading provider profiles...";
    if (searchText && appliedFilterCount === 0)
      return "No provider profiles found matching your search criteria.";
    if (!searchText && appliedFilterCount > 0)
      return "No provider profiles found matching filter criteria.";
    if (searchText && appliedFilterCount > 0)
      return "No provider profiles found matching search and filter criteria.";

    return "No provider profiles found.";
  }, [loading, searchText, appliedFilterCount]);

  useEffect(() => {
    if (
      location.state &&
      location.state.alertMessage &&
      location?.state?.fromView
    ) {
      setAlertMessage(location.state.alertMessage);
      setAlertType(location.state.alertType || "info");
      setAlertOpen(true);
      // to clear the state after showing the alert
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state && location.state.searchText) {
      // from view details page we are sending search text if that already applies in list page
      const searchValue = location.state.searchText;
      setSearchText(searchValue);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (
      location.state &&
      Object.keys(location?.state?.activeFilters || {}).length > 0
    ) {
      const filters = location.state.activeFilters;
      setActiveFilters(filters);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  useEffect(() => {
    const pageToUse = isSearchTriggered.current ? 1 : currentPage;
    fetchProviderProfiles(pageToUse, searchText || "", activeFilters);
    isSearchTriggered.current = false;
  }, [currentPage, searchText, activeFilters]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Box className="provider-profiles-main-wrapper">
      <ProfileHeader breadcrumbLink={breadcrumbLinkForList} />

      <Box
        className="provider-profiles-filter-and-list-wrapper"
        sx={{
          display: !isViewOpen ? "flex" : "none",
          flexDirection: "column",
          paddingBottom: totalRecords === 0 ? "10px" : "0px",
        }}
      >
        <Box className="provider-profiles-search-and-filters">
          <ProviderRequestSearchAndFilters
            onSearch={handleSearch}
            onFilterChange={setActiveFilters}
            appliedFiltersCount={appliedFilterCount}
            isFilterOpen={isOpenFilter}
            setIsFilterOpen={setIsOpenFilter}
            searchRef={searchRef}
            localSearchText={searchText}
            isReset={isResetSearch}
            placeholder="Search by Business Name, Contact Name, etc. "
          />
        </Box>

        <Box className="provider-profiles-listing-table-wrapper">
          <ListingTable
            headers={profileHeaders}
            data={formattedData?.map((item) => ({
              ...item,
            }))}
            onRowClick={(row) => {
              navigate(
                `${routes.PROVIDER_PROFILE_DETAILS.replace(":id", row.id)}`,
                {
                  state: {
                    searchText: searchText,
                    activeFilters: activeFilters,
                  },
                }
              );
            }}
            noResultsText={noResultsTxt}
          />

          <Box
            style={{ display: isOpenFilter ? "flex" : "none" }}
            className="provider-requests-filter-options filters-options-main"
          >
            <ProviderProfilesFilterOptions
              isOpenFilter={isOpenFilter}
              setIsOpenFilter={setIsOpenFilter}
              providerProfilesFilterOptions={providerProfileFilterOptions}
              activeFilters={{
                ...activeFilters,
                businessName: activeFilters?.businessName,
              }}
              onApplyFilters={(filters) => {
                setActiveFilters(filters);
                setCurrentPage(1); // Reset to page 1 on filter apply
              }}
            />
          </Box>
        </Box>

        {totalRecords > 0 && (
          <AdminPaginationComponent
            totalRecords={totalRecords}
            pageSize={PAGE_SIZE}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </Box>

      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default ProviderProfilesList;
